/**
 * Fragment de routes du paquet v4-p02-documents-ecrans-1 (vague V4 ; règle R2 du découpage V4–V11 : un fragment par
 * paquet, `routes/v4.tsx` interdit), rendu par le registre v10. Composants chargés à la demande, aucun code exécuté au
 * chargement. Routes du socle remplacées (`remplace: true`), d'après le `couvre: route:` des YAML d'écrans :
 *  - `/approvals` → projet-decisions (l'écran historique `ApprovalCenter` n'est plus servi) ;
 *  - `/approvals/:id` → projet-decision-fiche (l'écran historique `PageDecision` n'est plus servi) ;
 *  - `/editor` → projet-document-editeur (l'écran historique `SpecEditor` n'est plus servi) ;
 *  - `/design` → projet-design (l'écran historique `DesignScreen` n'est plus servi) ;
 *  - `/projet/classement/:nature/:id` → projet-classement-fiche (la fiche historique `ClassementFiche` n'est plus servie).
 * Routes neuves (objet par l'adresse, `?projet=` transmis) :
 *  - `/approvals/:id/confirmer` → dialogue-confirmer-decision ;
 *  - `/projet/documents/:documentId` → projet-document-fiche ; `…/generer` → dialogue-generer-elements ;
 *    `…/reviser` → dialogue-proposer-revision ;
 *  - `/projet/derogations/:derogationId` → projet-derogation-fiche.
 * App.tsx (gelé) et les fichiers des écrans historiques restent inchangés.
 */
import { lazy, Suspense, type ComponentType, type JSX } from 'react'

import type { Session } from '../../auth/session'
import type { ContexteRoutes, FragmentRoutes } from './registre'

const ProjetDecisions = lazy(() => import('../ecrans/v4/documents-1/ProjetDecisions'))
const ProjetDecisionFiche = lazy(() => import('../ecrans/v4/documents-1/ProjetDecisionFiche'))
const DialogueConfirmerDecision = lazy(() => import('../ecrans/v4/documents-1/DialogueConfirmerDecision'))
const ProjetDocumentFiche = lazy(() => import('../ecrans/v4/documents-1/ProjetDocumentFiche'))
const DialogueGenererElements = lazy(() => import('../ecrans/v4/documents-1/DialogueGenererElements'))
const DialogueProposerRevision = lazy(() => import('../ecrans/v4/documents-1/DialogueProposerRevision'))
const ProjetDocumentEditeur = lazy(() => import('../ecrans/v4/documents-1/ProjetDocumentEditeur'))
const ProjetClassementFiche = lazy(() => import('../ecrans/v4/documents-1/ProjetClassementFiche'))
const ProjetDerogationFiche = lazy(() => import('../ecrans/v4/documents-1/ProjetDerogationFiche'))
const ProjetDesign = lazy(() => import('../ecrans/v4/documents-1/ProjetDesign'))

function ecran(Composant: ComponentType<{ session: Session }>) {
  return ({ session, t }: ContexteRoutes): JSX.Element => (
    <Suspense fallback={<p role="status">{t('v10.primitives.assemblage.chargement')}</p>}>
      <Composant session={session} />
    </Suspense>
  )
}

export const fragment: FragmentRoutes = {
  zone: 'v4-documents-1',
  routes: [
    { chemin: '/approvals', element: ecran(ProjetDecisions), remplace: true },
    { chemin: '/approvals/:id', element: ecran(ProjetDecisionFiche), remplace: true },
    { chemin: '/approvals/:id/confirmer', element: ecran(DialogueConfirmerDecision) },
    { chemin: '/editor', element: ecran(ProjetDocumentEditeur), remplace: true },
    { chemin: '/design', element: ecran(ProjetDesign), remplace: true },
    { chemin: '/projet/classement/:nature/:id', element: ecran(ProjetClassementFiche), remplace: true },
    { chemin: '/projet/documents/:documentId', element: ecran(ProjetDocumentFiche) },
    { chemin: '/projet/documents/:documentId/generer', element: ecran(DialogueGenererElements) },
    { chemin: '/projet/documents/:documentId/reviser', element: ecran(DialogueProposerRevision) },
    { chemin: '/projet/derogations/:derogationId', element: ecran(ProjetDerogationFiche) },
  ],
}
